import React, {Suspense, useEffect} from 'react'
import ClientSuspense from '@components/ClientSuspense'
import PoweredByJusto from '@components/PoweredByJusto'
import Loading from '@components/SuspenseLoading/Loading'
import Card from '@components/V3/Card'
import SpinnerLoading from '@components/V3/loadingIndicators/Spinner'
import {EVENTS} from '@helpers/analytics/events'
import {PurchaseTaxonomies} from '@helpers/analytics/events/purchase'
import {sendEvent} from '@helpers/analytics/sendEvents'
import useEventsBaseProperties from '@hooks/useEventsBaseProperties'
import useShouldRedirectToPaymentGateway from '@hooks/useShouldRedirectToPaymentGateway.main'
import {useRouter} from 'next/router'
import {useTranslation} from 'next-i18next'

import Layout from './components/Layout'
import {Logo} from './components/Logo'
import useOrder from './hooks/useOrder.main'
import useOrderDetails from './sections/Order/useOrderDetails.main'
import Content, {ContentSkeleton} from './Content'
import Left from './Left'
import Typeform from './Typeform'

const TypeformMemo = React.memo(Typeform)

function TrackV2Inner() {
  const router = useRouter()
  const {orderId: orderIdQ} = router.query
  const orderId = Array.isArray(orderIdQ) ? orderIdQ[0] : orderIdQ
  const baseEventProperties = useEventsBaseProperties()
  const orderDetails = useOrderDetails()
  const {order} = useOrder()

  const {t} = useTranslation('tracking')
  const needsPayment = useShouldRedirectToPaymentGateway(orderId)

  useEffect(() => {
    if (needsPayment) {
      router.replace(`/payment-gateway/${orderId}`)
    }
  }, [needsPayment])

  useEffect(() => {
    if (!orderDetails?._id && !order?._id) return

    let productAmount = 0
    orderDetails?.items?.forEach(item => {
      productAmount += item.amount
    })
    sendEvent<PurchaseTaxonomies['trackingPageViewed']>(EVENTS.purchase.trackingPageViewed, {
      ...baseEventProperties,
      orderId,
      cartItemsAmount: orderDetails.items?.length ?? 0,
      productAmount,
      subTotalPrice: orderDetails.baseItemsPrice ?? 0,
      couponAmount: orderDetails.couponDiscount ?? 0,
      couponName: orderDetails.couponName ?? '',
      couponCode: orderDetails.couponCode ?? '',
      couponId: orderDetails.couponId ?? '',
      loyaltyAmount: orderDetails?.loyaltyTransaction?.amount ?? 0,
      tipAmount: orderDetails.tipAmount ?? 0,
      deliveryAmountToPay: order?.deliveryFee ?? 0,
      amountToPay: orderDetails.amountToPay ?? 0,
      isScheduledOrder: order?.isScheduled,
      scheduleDate: order?.timeText?.split(',')[0] ?? '',
      scheduleTime: order?.timeText?.split(',')[1] ?? '',
      timeLabel: order?.timeText ?? '',
      currentDeliveryDuration: orderDetails.deliveryDuration?.toString() ?? '',
      payStatus: order?.paymentStatus ?? '',
      payMethod: order?.paymentType,
      cardType: order?.transaction?.cardType ?? '',
      cardId: order?.transaction?.cardId ?? '',
      menuId: order?.menuId ?? ''
    })
  }, [orderDetails?._id, order?._id])

  if (needsPayment) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 h-screen">
        <SpinnerLoading size="lg" />
      </div>
    )
  }

  return (
    <Layout
      logo={<Logo />}
      left={
        <Suspense
          fallback={
            <div className="h-80 lg:h-full flex items-center justify-center">
              <SpinnerLoading />
            </div>
          }>
          <Left />
        </Suspense>
      }
      bottom={
        <Card transparentOnDesktop desktopThreshold="lg">
          <div className="py-4">
            <PoweredByJusto />
          </div>
        </Card>
      }>
      <Suspense fallback={<ContentSkeleton />}>
        <Content />
        <TypeformMemo />
      </Suspense>
    </Layout>
  )
}

export default function TrackV2() {
  return (
    <ClientSuspense fallback={<Loading />}>
      <TrackV2Inner />
    </ClientSuspense>
  )
}
